import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Autocomplete, Button, TextField, Typography, Box } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'

import { getContentUploadIntegrations } from '@/routes/Integration/integrationHelpers'
import { getIntegrationCourses } from '@/store/quizzes/actions'
import integrationService from '@/services/IntegrationService'
import { HelperService } from '@/services/helper.service'
import { capitalizeFirstLetter } from '@/utils/appUtils'
import { localDateTime } from '@/utils/formatUtils'
import CustomSelect from '@/components/UI/Select'
import ChildPageHeader from '@/components/UI/ChildPageHeader'

const helperService = new HelperService()

export interface ImportDocumentRef {
  onClickImport: () => void
}

interface Course {
  display_name: string
  id: string
  label: string
  name: string
  value: string
}

interface AsyncTask {
  _id: string
  organisation_id: string
  agent_id: string
  author_id: string
  task_id: string
  status: string | null
  created_at: string
  updated_at: string
}

const ImportLmsContent = React.forwardRef<ImportDocumentRef, any>(
  (props: any, _ref) => {
    const { t } = useTranslation('translation')
    const dispatch = useDispatch()
    const [selectedSource, setSelectedSource] = useState({
      label: 'Moodle',
      value: 'moodle',
    })
    const [loading, setLoading] = useState(false)
    const [sourceOptions, setSourceOptions] = useState([])
    const [courses, setCourses] = useState<Course[]>([])
    const [selectedCourse, setSelectedCourse] = useState(null)
    const [selectedCourseId, setSelectedCourseId] = useState('')
    const [isAuthenticated, setIsAuthenticated] = useState(true)
    const [contentIntegrations, setContentIntegrations] = useState<{
      [key: string]: { label: string; value: string }[]
    }>({})
    const [selectedContentIntegration, setSelectedContentIntegration] =
      useState<{
        label: string
        value: string
      }>({
        label: '',
        value: 'current',
      })
    const [asyncTasks, setAsyncTasks] = useState<AsyncTask[]>([])
    const [tasksLoading, setTasksLoading] = useState(false)
    const [paginationModel, setPaginationModel] = useState({
      page: 0,
      pageSize: 10,
    })
    const [totalTasks, setTotalTasks] = useState(0)

    const sourceLabels: { [index: string]: any } = {
      canvas: 'Canvas',
      blackboard: 'Blackboard',
      desire2learn: 'D2L Brightspace',
      moodle: 'Moodle',
    }

    const asyncTasksColumns: GridColDef[] = [
      {
        field: 'status',
        headerName: t('Status'),
        flex: 0.8,
        minWidth: 120,
        renderCell: params =>
          params.value === 'completed' ? t('Completed') : t('Pending'),
      },
      {
        field: 'created_at',
        headerName: t('Creation Date'),
        flex: 1,
        minWidth: 180,
        renderCell: params => localDateTime(params.value),
      },
      {
        field: 'updated_at',
        headerName: t('Last Updated'),
        flex: 1,
        minWidth: 180,
        renderCell: params => localDateTime(params.value),
      },
    ]

    const showContentIntegrations =
      selectedSource &&
      contentIntegrations.hasOwnProperty(selectedSource.value) &&
      contentIntegrations[selectedSource.value].length > 1

    useEffect(() => {
      fetchSourceOptions()
      fetchAsyncTasks()
    }, [])

    useEffect(() => {
      fetchAsyncTasks()
    }, [paginationModel.page, paginationModel.pageSize])

    const fetchAsyncTasks = async () => {
      try {
        setTasksLoading(true)
        const response = await integrationService.getAsyncTasks(
          'lmsimport_task',
          paginationModel.page + 1, // API is 1-based, DataGrid is 0-based
          paginationModel.pageSize,
        )
        if (response?.data?.asynctasks) {
          setAsyncTasks(response.data.asynctasks)
          setTotalTasks(response.data.pagination?.total_items || 0)
        }
      } catch (error) {
        console.error('Failed to fetch async tasks', error)
      } finally {
        setTasksLoading(false)
      }
    }

    const handleCourseChange = (newValue: Course | null) => {
      setSelectedCourse(newValue)
      handleCourseIdChange(newValue ? newValue.id : '')
    }

    const handleCourseIdChange = (newCourseId: string) => {
      setSelectedCourseId(newCourseId)
    }

    const handleSourceChange = (newSource: any) => {
      setSelectedSource(newSource)
      setSelectedContentIntegration({
        label: '',
        value: 'current',
      })
      setCourses([])

      if (
        !newSource ||
        !contentIntegrations.hasOwnProperty(newSource.value) ||
        contentIntegrations[newSource.value].length <= 1
      ) {
        handleSelectedSourceCourses(
          newSource.value,
          selectedContentIntegration.value,
        )
        handleCourseChange(null)
      }
    }

    const handleContentIntegrationChange = (newContentIntegration: any) => {
      setSelectedContentIntegration(newContentIntegration)
      setCourses([])
      handleSelectedSourceCourses(
        selectedSource.value,
        newContentIntegration.value,
      )
      handleCourseChange(null)
    }

    const fetchSourceOptions = async () => {
      setLoading(true)
      const integrations = await getContentUploadIntegrations()

      // Only include Moodle integrations
      const moodleIntegrations = (integrations || []).filter(
        (i: any) => i.provider === 'moodle',
      )

      let _sourceOptions: { [key: string]: { label: string; value: string } } =
        {}
      let _contentIntegrations: {
        [key: string]: { label: string; value: string }[]
      } = {}
      moodleIntegrations.forEach(
        (integration: { provider: string; name: string; _id: string }) => {
          if (!_sourceOptions.hasOwnProperty(integration.provider)) {
            _sourceOptions[integration.provider] = {
              label: sourceLabels.hasOwnProperty(integration.provider)
                ? sourceLabels[integration.provider]
                : capitalizeFirstLetter(integration.provider),
              value: integration.provider,
            }
          }

          if (!_contentIntegrations.hasOwnProperty(integration.provider)) {
            _contentIntegrations[integration.provider] = []
          }
          _contentIntegrations[integration.provider].push({
            label: integration.name,
            value: integration._id,
          })
        },
      )

      let __sourceOptions = Object.values(_sourceOptions)
      setSourceOptions(__sourceOptions)
      setContentIntegrations(_contentIntegrations)
      setLoading(false)
      if (__sourceOptions.length > 0) {
        handleSourceChange(__sourceOptions[0])
      }
    }

    const handleSelectedSourceCourses = async (
      provider: string,
      integrationId?: string,
    ) => {
      try {
        setLoading(true)
        const currentIntegrationCoursesResponse: any = await dispatch(
          getIntegrationCourses(provider, integrationId),
        )
        setCourses([
          ...currentIntegrationCoursesResponse.courses.map((d: any) => {
            return { ...d, label: d.display_name, value: d.id }
          }),
        ])
        setIsAuthenticated(true)
        setLoading(false)
      } catch (error: any) {
        if (error && error.status && error.status.code === 400) {
          setIsAuthenticated(false)
        }
        setLoading(false)
      }
    }

    useEffect(() => {
      const isDisabled = !selectedCourseId || loading
      props?.setIsImportButtonDisabled &&
        props.setIsImportButtonDisabled(isDisabled)
    }, [loading, selectedCourseId])

    return (
      <section className="build-knowledge-v2 page-mid-wraper h-without-foter">
        <ChildPageHeader
          title={t('Import Content from an LMS')}
          subTitle={t(
            'Choose an LMS and select a course to import content from your integrated platform',
          )}
        />
        <div className="integration-sources">
          <div className="select-course tw-grid tw-grid-cols-12 tw-gap-4 tw-mb-4">
            <div
              className={
                showContentIntegrations ? 'tw-col-span-3' : 'tw-col-span-6'
              }
            >
              <CustomSelect
                value={selectedSource?.value || ''}
                onChange={(_value, option) => handleSourceChange(option as any)}
                options={sourceOptions}
                placeholder={t('Select Source')}
              />
            </div>
            {showContentIntegrations && (
              <Autocomplete
                size="small"
                value={selectedContentIntegration}
                className="tw-col-span-3"
                onChange={(_event: any, newValue) => {
                  handleContentIntegrationChange(newValue)
                }}
                options={contentIntegrations[selectedSource.value]}
                renderInput={params => (
                  <TextField {...params} placeholder={t('Select Instance')} />
                )}
                placeholder={t('Select Instance')}
              />
            )}

            <div className="tw-col-span-6">
              <Autocomplete
                size="small"
                value={selectedCourse}
                inputValue={selectedCourseId}
                defaultValue={null}
                freeSolo
                clearOnEscape
                onChange={(_event: any, newValue: Course | null) => {
                  handleCourseChange(newValue)
                }}
                onInputChange={(_, val, reason) => {
                  if (reason === 'input') handleCourseIdChange(val)
                }}
                options={courses}
                // sx={{ width: showContentIntegrations ? '20%' : '30%' }}
                renderInput={params => (
                  <TextField {...params} placeholder={t('Select Course')} />
                )}
                // placeholder={t('Select Course')}
              />
            </div>
          </div>

          <div className="my-3">
            <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-gap-3">
              <Typography
                variant="body2"
                component="p"
                className="tw-text-sm tw-text-gray-600 tw-max-w-[760px] tw-mx-auto"
              >
                {t(
                  'The import runs asynchronously and replicates your LMS course structure by creating knowledge groups for modules / sections and importing files into their corresponding sections.',
                )}
                <br />
                {t(
                  'You will receive a notification once the task is complete.',
                )}
              </Typography>
              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    setLoading(true)
                    await integrationService.importCourseContent(
                      selectedSource.value,
                      selectedCourseId,
                      selectedContentIntegration?.value || 'current',
                    )
                    helperService.showToaster(
                      t(
                        'Import task created. You will receive a notification once the task is complete.',
                      ),
                      'success',
                    )
                    // Deselect course after creating task
                    setSelectedCourse(null)
                    setSelectedCourseId('')
                    // Refresh async tasks
                    fetchAsyncTasks()
                  } catch (e) {
                    // Optional: handle error state/logging
                    // console.error('Import failed', e)
                  } finally {
                    setLoading(false)
                  }
                }}
                disabled={!isAuthenticated || !selectedCourseId || loading}
              >
                {t('Import Course Content')}
              </Button>
            </div>
          </div>

          {/* Async Tasks Table */}
          {totalTasks > 0 && (
            <div className="tw-mt-8">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <Typography variant="subtitle2" component="h2">
                  {t('Import History')}
                </Typography>
              </div>
              <Box sx={{ height: 500, mt: 1 }}>
                <DataGrid
                  rows={asyncTasks}
                  columns={asyncTasksColumns}
                  loading={tasksLoading}
                  getRowId={row => row._id}
                  paginationMode="server"
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  rowCount={totalTasks}
                  pageSizeOptions={[5, 10, 20, 50]}
                  disableRowSelectionOnClick
                />
              </Box>
            </div>
          )}
        </div>
      </section>
    )
  },
)

export default ImportLmsContent
